import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db7sjjitp9sllopf7jog";

const features = [
  { icon: "ri-newspaper-line", title: "资讯聚合", desc: "汇总影视动态、幕后揭秘与行业观察，第一时间掌握观影风向。" },
  { icon: "ri-file-text-line", title: "剧情详解", desc: "提供详尽的剧情简介与基本信息，帮助你在观影前做出选择。" },
  { icon: "ri-filter-3-line", title: "多维筛选", desc: "按地区、年份、评分与标签组合筛选，快速定位感兴趣的作品。" },
  { icon: "ri-bookmark-3-line", title: "专题推荐", desc: "围绕主题与类型策划合集，成体系地发现更多好作品。" },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 缘之空影视资讯平台",
    description:
      "了解缘之空影视资讯平台的定位与内容特色，并通过留言表单与我们取得联系。缘之空专注电影电视剧资讯、剧情介绍与影视推荐。",
    keywords: "缘之空,关于我们,影视资讯平台,联系我们,影视推荐",
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      formEl.reset();
      return;
    }

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();
    if (!name || !email || !message) {
      setStatus("error");
      setFormError("请填写姓名、邮箱和留言内容后再提交。");
      return;
    }

    setStatus("submitting");
    setFormError("");
    try {
      formData.delete("website_alt");
      const body = new URLSearchParams();
      formData.forEach((value, key) => body.append(key, String(value)));

      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const looksSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !looksSpam;

      if (ok) {
        setStatus("success");
        formEl.reset();
      } else {
        setStatus("error");
        setFormError(typeof serverMsg === "string" && serverMsg ? serverMsg : "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="w-full px-4 md:px-6 py-8 md:py-10">
      <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
        <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
        <i className="ri-arrow-right-s-line text-foreground-300"></i>
        <span className="text-foreground-800">关于我们</span>
      </nav>

      <section className="mb-12">
        <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">关于缘之空</h1>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed max-w-3xl mb-4">
          缘之空是一个面向影视爱好者的高清电影电视剧资讯与剧情介绍平台。我们希望把分散在各处的影视动态、剧情解析与推荐整合到一起，用清晰的分类和多维筛选，让每一次选片都更有依据。
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed max-w-3xl">
          平台内容涵盖电影与电视剧多个类型，无论是追最新的热门剧集，还是重温经得起时间考验的高分经典，你都能在这里找到贴近观影需求的参考信息。本站目前仅提供资讯与剧情内容展示，不提供播放或下载服务。
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-5">平台特色</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {features.map((f) => (
            <div key={f.title} className="bg-background-50 border border-background-200 rounded-lg p-5">
              <span className="w-11 h-11 rounded-md bg-primary-500 text-background-50 flex items-center justify-center mb-4">
                <i className={`${f.icon} text-xl`}></i>
              </span>
              <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">{f.title}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 mb-12">
        <div>
          <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-5">联系我们</h2>
          <p className="text-sm text-foreground-600 leading-relaxed mb-6">
            如果你对平台内容有任何建议、想推荐影视作品，或希望与我们合作，欢迎通过下面的留言表单联系我们，我们会尽快回复。
          </p>
          <ul className="space-y-4">
            <li className="flex items-center gap-3 text-sm text-foreground-700">
              <span className="w-10 h-10 rounded-md bg-background-100 flex items-center justify-center text-primary-500">
                <i className="ri-mail-line text-lg"></i>
              </span>
              contact@yuanzhikong.example
            </li>
            <li className="flex items-center gap-3 text-sm text-foreground-700">
              <span className="w-10 h-10 rounded-md bg-background-100 flex items-center justify-center text-primary-500">
                <i className="ri-map-pin-line text-lg"></i>
              </span>
              在线运营 · 面向全国影视爱好者
            </li>
            <li className="flex items-center gap-3 text-sm text-foreground-700">
              <span className="w-10 h-10 rounded-md bg-background-100 flex items-center justify-center text-primary-500">
                <i className="ri-time-line text-lg"></i>
              </span>
              工作日 09:00 - 18:00 回复
            </li>
          </ul>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6">
          <h2 className="font-heading text-lg font-bold text-foreground-950 mb-1">留言反馈</h2>
          <p className="text-xs text-foreground-500 mb-5">带 <span className="text-accent-600">*</span> 为必填项，留言内容不超过 500 字。</p>

          {status === "success" ? (
            <div className="flex flex-col items-center text-center py-10">
              <span className="w-14 h-14 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center mb-4">
                <i className="ri-checkbox-circle-line text-2xl"></i>
              </span>
              <h3 className="font-heading text-base font-bold text-foreground-900 mb-1">留言已提交</h3>
              <p className="text-sm text-foreground-500 mb-5">感谢你的反馈，我们会尽快查看并回复。</p>
              <button
                type="button"
                onClick={() => setStatus("idle")}
                className="px-4 py-2 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 transition-colors cursor-pointer"
              >
                再写一条
              </button>
            </div>
          ) : (
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} noValidate className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm text-foreground-700 mb-1.5">姓名 <span className="text-accent-600">*</span></label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  autoComplete="name"
                  placeholder="请输入你的称呼"
                  className="w-full bg-background-100 border border-background-300 rounded-md px-3.5 py-2.5 text-sm text-foreground-800 outline-none focus:border-primary-400 placeholder:text-foreground-400"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm text-foreground-700 mb-1.5">邮箱 <span className="text-accent-600">*</span></label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="用于接收回复，例如 you@example.com"
                  className="w-full bg-background-100 border border-background-300 rounded-md px-3.5 py-2.5 text-sm text-foreground-800 outline-none focus:border-primary-400 placeholder:text-foreground-400"
                />
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm text-foreground-700 mb-1.5">反馈类型</label>
                <select
                  id="subject"
                  name="subject"
                  defaultValue="内容反馈"
                  className="w-full bg-background-100 border border-background-300 rounded-md px-3.5 py-2.5 text-sm text-foreground-800 outline-none focus:border-primary-400 cursor-pointer"
                >
                  <option value="内容反馈">内容反馈</option>
                  <option value="影视推荐">影视推荐</option>
                  <option value="合作咨询">合作咨询</option>
                  <option value="其他建议">其他建议</option>
                </select>
              </div>
              <div>
                <label htmlFor="message" className="block text-sm text-foreground-700 mb-1.5">留言内容 <span className="text-accent-600">*</span></label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="请描述你的建议或需求（不超过 500 字）"
                  className="w-full bg-background-100 border border-background-300 rounded-md px-3.5 py-2.5 text-sm text-foreground-800 outline-none focus:border-primary-400 placeholder:text-foreground-400 resize-none"
                ></textarea>
              </div>

              <input
                className="form-extra-field"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {status === "error" && formError && (
                <p className="text-sm text-accent-700 bg-accent-100/70 rounded-md px-3 py-2">
                  <i className="ri-error-warning-line mr-1.5"></i>
                  {formError}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full flex items-center justify-center gap-1.5 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
              >
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    提交中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    提交留言
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>

      <section className="bg-background-100 border border-background-200 rounded-lg p-5 md:p-6">
        <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">使用说明与版权声明</h2>
        <div className="space-y-3 text-sm text-foreground-600 leading-relaxed">
          <p>本站所有影视资讯、剧情简介与推荐内容均为演示示例，仅供学习与参考，不构成任何观看或购买建议。</p>
          <p>本站不提供影视资源的播放与下载服务，相关影视名称与信息版权归各自权利人所有。如内容涉及权益问题，欢迎通过上方表单联系我们处理。</p>
          <p>站内“收藏”等功能为前端演示效果，不会实际存储你的操作数据。</p>
        </div>
      </section>
    </div>
  );
}